'use client';
import { LeagueEvent } from '@interfaces/APITravel';
import { useEffect, useState } from 'react';
import { ArenaView, Sidebar } from './components';

import { SELECTED_FLIGHTS_STORAGE } from '@config/storageKeys';
import tevoRoutes from '@config/tevoRoutes';
import { EventLoaderResponse } from '@hooks/use-event-loader';
import { usePackagesParams } from '@hooks/use-packages-params';
import { useScrollToTopOnMount } from '@hooks/use-scroll-top-on-mount';
import useBuildPackagePrices from '@hooks/useBuildPackagePrices';
import useStorage from '@hooks/useStorage';
import { apiFetcher } from '@instance';
import { PageLoader } from '@v2/components';
import { BuyPackageHeader } from '@v2/components/BuyPackageHeader/BuyPackageHeader';
import { CheckoutHeaderQuote } from '@v2/components/BuyPackageHeader/checkout-header-quote';
import { cn } from '@v2/utils';
import { useTicketsStore } from 'src/stores/tickets-store';
import useSWR from 'swr';
import { useMediaQuery } from 'usehooks-ts';
import { getFlightFees } from '../checkout/containers/CheckoutAsideCards/components/util';
import { PreBookingPayload } from '../flights/components/FlightsContent/FlightDrawer/FlightFares';
import { TicketSelectionBottomHeader } from './components/TicketSelectionBottomHeader';
import { EventSalesBanner } from './components/EventSalesBanner';

type PageProps = {
  event: LeagueEvent;
  eventId: string;
  ticketsSold: number | null;
};

export function SearchTicketView({ event, eventId, ticketsSold }: PageProps) {
  useScrollToTopOnMount();
  const {
    data: ticketData,
    error,
    isLoading,
  } = useSWR<EventLoaderResponse[]>(
    tevoRoutes.getEventProxy(eventId, true),
    apiFetcher,
    {
      revalidateOnFocus: false,
      revalidateIfStale: false,
      revalidateOnReconnect: false,
    }
  );

  const { packageType } = usePackagesParams();

  const storage = useStorage();
  const [eventData, tickets] = ticketData || [];
  const ticketGroups = tickets?.ticket_groups || [];
  const ticketsQuantity = useTicketsStore((state) => state.ticketsQuantity);
  const setTicketSelected = useTicketsStore((state) => state.setTicketSelected);
  const [estimate, setEstimate] = useState<CheckoutHeaderQuote>({
    amount: null,
    travelers: ticketsQuantity,
    currency: 'USD',
  });

  const {
    ticketPrice,
    hotelPrice,
    isLoading: isLoadingPrices,
  } = useBuildPackagePrices({
    ticketsCount: ticketsQuantity,
    selectedHotelPrice: null,
    selectedTicketPrice: null,
    isBundle: packageType === 'packages',
  });

  const [minFlightPrice] = useState(() => {
    const selectedFlights = JSON.parse(
      storage.getItem(SELECTED_FLIGHTS_STORAGE) || '{}'
    ) as PreBookingPayload;
    if (!selectedFlights) return 0;
    const { simpleFare } = selectedFlights;
    if (!simpleFare) return 0;

    const { packagePrice } = simpleFare;

    const flightFees = getFlightFees(packagePrice);
    return Number(packagePrice) + flightFees;
  });

  const isMobile = useMediaQuery('(max-width: 1024px)');

  useEffect(() => {
    const selectedFlights = JSON.parse(
      storage.getItem(SELECTED_FLIGHTS_STORAGE) || '{}'
    ) as PreBookingPayload;
    if (selectedFlights?.passengers) {
      const totalPassengers =
        selectedFlights.passengers.adults +
        selectedFlights.passengers.children +
        selectedFlights.passengers.infants;
      if (totalPassengers > 0) {
        useTicketsStore.setState({ ticketsQuantity: totalPassengers });
      }
    }

    setTicketSelected(null);
  }, [storage, setTicketSelected]);

  return (
    <>
      <BuyPackageHeader event={event} quote={estimate} />
      {isLoading || isLoadingPrices ? (
        <PageLoader />
      ) : (
        <>
          <EventSalesBanner ticketsSold={ticketsSold} />
          <section className="buy-package-height pt-2 lg:mx-auto lg:h-[720px] lg:w-[1400px] lg:px-20 lg:pt-6">
            <div className="flex h-auto flex-col-reverse rounded-lg border border-gray-50 lg:h-[720px] lg:flex-row lg:items-start">
              <>
                <aside
                  className={cn(
                    'h-full w-full lg:w-[40%]',
                    'border-gray-200 lg:border-r'
                  )}
                >
                  <Sidebar
                    ticketsList={ticketGroups}
                    loading={isLoading}
                    event={event}
                  />
                </aside>
                {!isMobile && (
                  <div className="h-full w-full bg-white lg:w-[60%]">
                    <ArenaView event={event} ticketGroups={ticketGroups} />
                  </div>
                )}
              </>
            </div>
          </section>

          <TicketSelectionBottomHeader
            event={event}
            minHotelPrice={hotelPrice}
            minTicketPrice={ticketPrice}
            selectedFlightPrice={minFlightPrice}
            onEstimate={setEstimate}
          />
        </>
      )}
    </>
  );
}
